import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { type Button, buttonVariants } from '../Button';

function Pagination({ className, ...props }: React.ComponentProps<'nav'>): React.ReactElement {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  );
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<'ul'>): React.ReactElement {
  return (
    <ul
      data-slot="pagination-content"
      className={cn('flex items-center gap-2', className)}
      {...props}
    />
  );
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>): React.ReactElement {
  return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
  isActive?: boolean;
  disabled?: boolean;
  variant?: 'terciary' | 'ghost';
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
  React.ComponentProps<'a'>;

function PaginationLink({
  className,
  isActive,
  children,
  disabled,
  variant = 'terciary',
  onClick,
  ...props
}: PaginationLinkProps): React.ReactElement {
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const pageNumber =
    typeof children === 'string' || typeof children === 'number' ? children : undefined;
  const ariaLabel = pageNumber
    ? isActive
      ? t('Pagination.currentPage', { page: pageNumber })
      : t('Pagination.goToPage', { page: pageNumber })
    : props['aria-label'] || (children ? undefined : 'Pagination link');

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>): void => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent<HTMLAnchorElement>);
    }
  };

  return (
    <a
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-current={isActive ? 'page' : undefined}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      data-slot="pagination-link"
      data-active={isActive}
      data-disabled={disabled}
      className={cn(
        disabled ? 'cursor-not-allowed opacity-50 pointer-events-none' : 'cursor-pointer',
        buttonVariants({
          variant,
          icon: 'only',
        }),
        isActive && 'bg-plum-100',
        'notranslate px-4',
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...props}
      translate="no"
    >
      {children}
    </a>
  );
}

function PaginationPrevious({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>): React.ReactElement {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      className={cn('px-0', className)}
      variant="ghost"
      {...props}
    >
      <ChevronLeftIcon className="size-5" />
    </PaginationLink>
  );
}

function PaginationNext({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>): React.ReactElement {
  return (
    <PaginationLink
      aria-label="Go to next page"
      className={cn('px-0', className)}
      variant="ghost"
      {...props}
    >
      <ChevronRightIcon className="size-5" />
    </PaginationLink>
  );
}

function PaginationEllipsis({
  className,
  ...props
}: Omit<React.ComponentProps<typeof PaginationLink>, 'children'>): React.ReactElement {
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  return (
    <PaginationLink
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn('flex justify-center w-10', className)}
      {...props}
    >
      <MoreHorizontalIcon className="mt-1.5" />
      <span className="sr-only">{t('Pagination.ellipsis')}</span>
    </PaginationLink>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
